// #141 Araç Detay paneli (sağ, geniş) — Planlanan Araç ile seçilen aracın bilgileri.
// Başlık: plaka + DAĞITIM KANALI seçici (plaka yanında; plan kanalına varsayılan) + kapasite barları.
//   NOT: bu seçici gerçek Distribütör (Vendor) tanımlarını listeler — Dağıtıcı (Distributor) entity'sini DEĞİL.
//   Plandaki tüm araçlar zaten seçili tek kanala aittir; alan bu kanalı gösterir.
// İki sekme:
//   1) Yüklenen Siparişler — geniş kompakt tablo (#, KG, Hacim, Kod, Ad, Tabela, Mahalle, Adres,
//      Fat./İrs., çıkar). Üstte arama. Drop target: buraya bırakılan sipariş bu araca yüklenir.
//   2) Rota Detayı — yalnız özet + harita (Haritayı Odakla + Tam Ekran). Müşteri listesi yok.
import { useEffect, useMemo, useRef, useState } from 'react';
import { authHeaders } from '../listPageStyles';
import { SearchableSelect } from '../ui';
import RouteMap from '../routePlan/RouteMap';
import { DocTypeChip } from './OrderCard';
import {
  API_BASE, unwrap, vehicleTotals, fillColor, fmtNumber, fmtMoney,
  makeLookupFetch, filterOrders,
} from './shipmentPlanningUtils';

const keyOf = (v) => v.id || v.shipmentVehicleId;

export default function VehicleDetailPanel({
  vehicle, onVendorChange, planVendorId, planVendorName,
  selectedOrderId, onSelectOrder, onDocTypeChange,
  onDragStart, onDragEnd, onDropHere, onRemoveOrder,
  onOptimize, onKmChange,
  checkedIds, onToggleChecked, onToggleAll,
}) {
  const [tab, setTab] = useState('orders');
  const [over, setOver] = useState(false);
  const [query, setQuery] = useState('');
  // #214 Bu seçici Distribütör (Vendor) tanımlarını listeler — Dağıtıcı (Distributor) DEĞİL.
  const vendorFetch = useMemo(() => makeLookupFetch('/api/vendors', { authHeaders }), []);

  const sortedOrders = useMemo(
    () => [...((vehicle && vehicle.orders) || [])].sort((a, b) => (a.visitOrderNo ?? 9999) - (b.visitOrderNo ?? 9999)),
    [vehicle],
  );
  const filtered = useMemo(() => filterOrders(sortedOrders, query), [sortedOrders, query]);

  if (!vehicle) {
    return (
      <div style={panelWrap}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary,#94a3b8)', fontSize: 13, padding: 24, textAlign: 'center', lineHeight: 1.6 }}>
          Yukarıdan <b>Planlanan Araç</b> seçtiğinizde aracın yüklü siparişleri ve rotası burada görünür.
        </div>
      </div>
    );
  }

  const totals = vehicleTotals(vehicle);
  const weightRatio = vehicle.maxWeight > 0 ? totals.weight / vehicle.maxWeight : 0;
  const volumeRatio = vehicle.volume > 0 ? totals.volume / vehicle.volume : 0;
  const loadRatio = Math.max(weightRatio, volumeRatio);
  const statusMeta = (vehicle.maxWeight <= 0 && vehicle.volume <= 0)
    ? { label: 'Kapasite tanımsız', color: '#94a3b8' }
    : loadRatio > 1 ? { label: 'Aşırı Yük', color: 'var(--danger)' }
      : loadRatio > 0.85 ? { label: 'Doluyor', color: 'var(--warn)' }
        : { label: 'Uygun', color: '#16a34a' };

  return (
    <div style={panelWrap}>
      {/* Başlık: plaka + dağıtıcı + durum */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: '1px solid var(--border-color,#e5e7eb)', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span aria-hidden="true" style={{ fontSize: 17 }}>🚚</span>
          <b style={{ fontSize: 15, fontWeight: 900, letterSpacing: 0.3, color: 'var(--text-primary,#0f172a)', fontFamily: "'Sora','Manrope',sans-serif" }}>{vehicle.plaka}</b>
        </span>
        {[vehicle.brand, vehicle.model].filter(Boolean).length > 0 && (
          <span style={{ fontSize: 11.5, color: 'var(--text-secondary,#94a3b8)', fontWeight: 600 }}>{[vehicle.brand, vehicle.model].filter(Boolean).join(' ')}</span>
        )}
        {/* Distribütör — plaka yanında; plandaki kanala (Vendor) varsayılan. */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 240 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary,#64748b)', whiteSpace: 'nowrap' }}>Distribütör:</span>
          <div style={{ minWidth: 180 }}>
            <SearchableSelect
              value={vehicle.vendorId || planVendorId || ''}
              selectedLabel={vehicle.vendorName || planVendorName || ''}
              onChange={(v, item) => onVendorChange?.(vehicle, v || null, item?.label || '')}
              fetchOptions={vendorFetch}
              pageSize={20}
              placeholder="Dağıtım kanalı ara…"
            />
          </div>
        </div>
        <span style={{ marginLeft: 'auto', fontSize: 11.5, fontWeight: 800, color: statusMeta.color, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusMeta.color }} /> {statusMeta.label}
        </span>
      </div>

      {/* Kapasite barları */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '10px 14px', borderBottom: '1px solid var(--border-color,#eef2f7)' }}>
        <FillBar label="Ağırlık" value={totals.weight} max={vehicle.maxWeight} unit="kg" />
        <FillBar label="Hacim" value={totals.volume} max={vehicle.volume} unit="m³" />
      </div>

      {/* Sekmeler */}
      <div style={{ display: 'flex', gap: 6, padding: '8px 12px 0' }}>
        <TabBtn active={tab === 'orders'} onClick={() => setTab('orders')}>📦 Yüklenen Siparişler ({totals.count})</TabBtn>
        <TabBtn active={tab === 'route'} onClick={() => setTab('route')}>🧭 Rota Detayı</TabBtn>
      </div>

      {tab === 'orders' ? (
        <OrdersTab
          vehicle={vehicle}
          orders={filtered}
          rawCount={sortedOrders.length}
          query={query} setQuery={setQuery}
          over={over} setOver={setOver}
          selectedOrderId={selectedOrderId}
          onSelectOrder={onSelectOrder}
          onDocTypeChange={onDocTypeChange}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDropHere={onDropHere}
          onRemoveOrder={onRemoveOrder}
          checkedIds={checkedIds}
          onToggleChecked={onToggleChecked}
          onToggleAll={onToggleAll}
        />
      ) : (
        <RouteTab vehicle={vehicle} sortedOrders={sortedOrders} onOptimize={onOptimize} onKmChange={onKmChange} onRemoveOrder={onRemoveOrder} />
      )}
    </div>
  );
}

// ── Sekme 1: Yüklenen Siparişler — geniş kompakt tablo ───────────────────────
const COLS = '26px 52px 70px 64px 92px minmax(120px,1.4fr) minmax(90px,1fr) minmax(90px,1fr) minmax(120px,1.6fr) 74px 28px';

// #152 madde 10 — müşteri E-Fatura / E-İrsaliye mükellefi ise dağıtım sırası yanına küçük simge.
function EDocFlags({ order }) {
  if (!order.isEinvoice && !order.isEdispatch) return null;
  const badge = (color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 1, fontSize: 8.5, fontWeight: 800,
    color, lineHeight: 1, padding: '1px 3px', borderRadius: 5, background: `${color}1a`, border: `1px solid ${color}40`,
  });
  return (
    <span style={{ display: 'inline-flex', gap: 2, marginTop: 2 }}>
      {order.isEinvoice && <span title="E-Fatura mükellefi" style={badge('#0073ea')}><span aria-hidden="true">🌐</span>F</span>}
      {order.isEdispatch && <span title="E-İrsaliye mükellefi" style={badge('#7a3cff')}><span aria-hidden="true">🌐</span>İ</span>}
    </span>
  );
}

function OrdersTab({
  vehicle, orders, rawCount, query, setQuery, over, setOver,
  selectedOrderId, onSelectOrder, onDocTypeChange, onDragStart, onDragEnd, onDropHere, onRemoveOrder,
  checkedIds, onToggleChecked, onToggleAll,
}) {
  // "Tümünü seç" görünürdeki (aramayla süzülmüş) siparişler üzerinden çalışır.
  const allChecked = orders.length > 0 && orders.every((o) => checkedIds?.has(o.orderId));
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDropHere?.(); }}
      style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: over ? 'rgba(0,115,234,0.04)' : 'transparent' }}
    >
      {/* Arama */}
      <div style={{ padding: '8px 12px' }}>
        <div style={{ position: 'relative' }}>
          <span aria-hidden="true" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#94a3b8' }}>🔍</span>
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Mahalle, kod, ünvan, tabela, adres, temsilci ile filtrele…"
            style={{ width: '100%', boxSizing: 'border-box', padding: '7px 26px 7px 28px', borderRadius: 9, border: '1px solid var(--border-color,#cbd5e1)', fontSize: 12, background: 'var(--input-bg,#fff)', color: 'var(--text-primary,#1f2937)' }} />
          {query && <button type="button" onClick={() => setQuery('')} aria-label="Temizle" style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', fontSize: 15 }}>×</button>}
        </div>
      </div>

      {rawCount === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'var(--text-secondary,#94a3b8)', fontSize: 12.5, padding: 20, lineHeight: 1.6, margin: '0 12px 12px', border: '1.5px dashed var(--border-color,#e5e7eb)', borderRadius: 12 }}>
          Bu araca sipariş yüklenmedi. Bekleyen siparişlerden sürükleyip buraya bırakın.
        </div>
      ) : (
        <div style={{ flex: 1, overflow: 'auto', margin: '0 12px 12px', border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 12 }}>
          {/* Tablo başlığı */}
          <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 6, padding: '7px 10px', position: 'sticky', top: 0, zIndex: 1, background: 'var(--grid-canvas,#f9fafb)', borderBottom: '1px solid var(--border-color,#e5e7eb)', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary,#6b7280)', letterSpacing: 0.2 }}>
            <div>
              <input type="checkbox" checked={allChecked} aria-label="Tümünü seç"
                title="Görünen siparişlerin tamamını seç"
                onChange={(e) => onToggleAll?.(orders, e.target.checked)}
                style={{ cursor: 'pointer' }} />
            </div>
            <div>#</div>
            <div style={{ textAlign: 'right' }}>KG</div>
            <div style={{ textAlign: 'right' }}>Hacim</div>
            <div>Kod</div>
            <div>Müşteri</div>
            <div>Tabela</div>
            <div>Mahalle</div>
            <div>Adres</div>
            <div>Belge</div>
            <div />
          </div>
          {orders.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-secondary,#94a3b8)', fontSize: 12.5 }}>Aramayla eşleşen sipariş yok.</div>
          ) : orders.map((o, idx) => {
            const checked = !!checkedIds?.has(o.orderId);
            return (
            <div
              key={o.orderId}
              draggable
              onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; onDragStart?.(o); }}
              onDragEnd={() => onDragEnd?.()}
              onClick={() => onSelectOrder?.(o)}
              style={{
                display: 'grid', gridTemplateColumns: COLS, gap: 6, padding: '6px 10px', alignItems: 'center',
                borderBottom: '1px solid var(--border-color,#f1f5f9)', fontSize: 12, cursor: 'grab',
                background: checked ? 'rgba(122,60,255,0.07)'
                  : o.orderId === selectedOrderId ? 'rgba(0,115,234,0.07)'
                    : o.__adjusted ? 'rgba(253,171,61,0.06)' : 'transparent',
              }}
            >
              <div onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={checked} onChange={() => onToggleChecked?.(o)}
                  aria-label="Siparişi seç" style={{ cursor: 'pointer' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: '50%', background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 10.5 }}>{o.visitOrderNo ?? idx + 1}</span>
                <EDocFlags order={o} />
              </div>
              <div style={{ textAlign: 'right', fontWeight: 700 }}>{fmtNumber(o.totalWeight)}</div>
              <div style={{ textAlign: 'right', color: 'var(--text-secondary,#64748b)' }}>{fmtNumber(o.totalVolume, 3)}</div>
              <div style={{ color: 'var(--text-secondary,#64748b)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.customerCode || '—'}</div>
              <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={o.customerName}>{o.customerName}</div>
              <div style={{ color: 'var(--text-secondary,#64748b)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={o.shortName}>{o.shortName || '—'}</div>
              <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={o.neighborhood}>{o.neighborhood || '—'}</div>
              <div style={{ color: 'var(--text-secondary,#64748b)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={o.address}>{o.address || '—'}</div>
              <div onClick={(e) => e.stopPropagation()}><DocTypeChip value={o.documentType} onToggle={(v) => onDocTypeChange?.(o, v)} /></div>
              <button type="button" title="Araçtan çıkar (Bekleyen'e)" onClick={(e) => { e.stopPropagation(); onRemoveOrder?.(o); }}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--danger)', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Sekme 2: Rota Detayı — özet + harita (tam ekran + pin'den çıkarma) ────────
// #152: "Haritayı Odakla" kaldırıldı (madde 8); pin balonundan ÇIKART (madde 7);
// Tam Ekran artık haritayı gerçekten kaplar (RouteMap height='100%' düzeltmesiyle, madde 6).
function RouteTab({ vehicle, sortedOrders, onOptimize, onKmChange, onRemoveOrder }) {
  const [km, setKm] = useState(null);
  const [geometry, setGeometry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const reqRef = useRef(0);

  const points = useMemo(() => sortedOrders
    .map((o, i) => ({ lat: o.customerLat, lng: o.customerLng, order: i + 1, color: '#0073ea', label: o.customerName, key: o.orderId }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)), [sortedOrders]);
  const totals = vehicleTotals(vehicle);
  const withCoords = points.length;
  const etaMin = km != null && km > 0 ? Math.round((km / 40) * 60) : 0;
  const pointsKey = points.map((p) => `${p.lat},${p.lng}`).join('|');

  useEffect(() => {
    if (points.length < 2) { setKm(points.length ? 0 : null); setGeometry(null); onKmChange?.(vehicle, 0); return undefined; }
    let alive = true;
    const reqId = ++reqRef.current;
    setLoading(true);
    fetch(`${API_BASE}/route`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ points: points.map((p) => ({ lat: p.lat, lng: p.lng })) }) })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        if (!alive || reqRef.current !== reqId) return;
        const data = unwrap(d) || {};
        const kmVal = Number(data.km) || 0;
        setKm(kmVal);
        setGeometry(data.geometryGeoJson || data.GeometryGeoJson || null);
        onKmChange?.(vehicle, kmVal);
      })
      .catch(() => { if (alive && reqRef.current === reqId) { setKm(null); setGeometry(null); } })
      .finally(() => { if (alive && reqRef.current === reqId) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointsKey]);

  // Pin balonundaki "Rotadan çıkar" → siparişi araçtan çıkar (Bekleyen'e döner).
  const handlePointRemove = (p) => {
    const o = sortedOrders.find((x) => x.orderId === p.key);
    if (o) onRemoveOrder?.(o);
  };

  // fullscreen değişiminde haritayı remount edip yeniden fit ettir (boyut değişir).
  const mapNode = (height) => (
    <RouteMap key={fullscreen ? 'fs' : 'inline'} points={points} numbered showLine geometry={geometry} height={height} onPointRemove={handlePointRemove} />
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto', padding: '10px 14px 14px' }}>
      {/* Özet pill'leri */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        <Pill color="#14b8a6">{sortedOrders.length} durak</Pill>
        <Pill color="#16a34a">{withCoords}/{sortedOrders.length} koordinat</Pill>
        <Pill color="#0f172a">{fmtNumber(totals.weight)} kg</Pill>
        <Pill color="#7c3aed">{fmtNumber(totals.volume, 3)} m³</Pill>
        <Pill color="#b45309">{fmtMoney(totals.amount)} ₺</Pill>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 800, color: 'var(--text-secondary,#64748b)' }}>
          <span aria-hidden="true">🕐</span>{loading ? '… km' : km != null ? `${fmtNumber(km, 1)} km · ~${etaMin} dk` : '—'}
        </span>
      </div>

      {/* Aksiyonlar — Odakla kaldırıldı; haritadaki müşteriye tıklayıp çıkartabilirsiniz. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
        <button type="button" onClick={() => setFullscreen(true)} disabled={withCoords === 0} style={miniBtn('#0073ea', withCoords === 0)}>⛶ Tam Ekran</button>
        <button type="button" onClick={onOptimize} disabled={sortedOrders.length < 2} style={miniBtn('#fdab3d', sortedOrders.length < 2)}>✨ Rona AI Optimize</button>
        {withCoords > 0 && (
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary,#94a3b8)' }}>
            İpucu: Haritada müşteriye tıklayıp <b style={{ color: 'var(--danger)' }}>Rotadan çıkar</b> ile çıkarabilirsiniz.
          </span>
        )}
      </div>

      {/* Harita */}
      {sortedOrders.length > 0 && withCoords === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 12, background: 'rgba(253,171,61,0.10)', border: '1px solid rgba(253,171,61,0.35)', color: '#92400e', fontSize: 12, fontWeight: 600, lineHeight: 1.5 }}>
          <span aria-hidden="true">⚠️</span> Müşteri koordinatları bulunamadı. Adres bilgileri eksik veya hatalı.
        </div>
      ) : sortedOrders.length === 0 ? (
        <div style={{ color: 'var(--text-secondary,#94a3b8)', fontSize: 12.5, padding: 20, textAlign: 'center', border: '1.5px dashed var(--border-color,#e5e7eb)', borderRadius: 12 }}>
          Bu araca atanmış durak yok. Önce sipariş yükleyin.
        </div>
      ) : (
        <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--border-color,#e5e7eb)' }}>
          {mapNode(360)}
        </div>
      )}

      {/* Tam ekran harita — haritayı gerçekten kaplar */}
      {fullscreen && (
        <div onClick={() => setFullscreen(false)} style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(15,23,42,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', width: '94vw', height: '88vh', background: 'var(--bg-card,#fff)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 4100, display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setFullscreen(false)} style={{ ...miniBtn('#e2445c'), boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>✕ Kapat</button>
            </div>
            {mapNode('100%')}
          </div>
        </div>
      )}
    </div>
  );
}

function FillBar({ label, value, max, unit }) {
  const ratio = max > 0 ? value / max : 0;
  const pct = Math.round(ratio * 100);
  const color = fillColor(ratio);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
        <span style={{ color: 'var(--text-secondary,#64748b)' }}>{label}</span>
        <span style={{ color }}>{fmtNumber(value)} / {fmtNumber(max)} {unit} <b style={{ marginLeft: 2 }}>%{pct}</b></span>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: 'var(--border-color,#eef2f7)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(100, pct)}%`, borderRadius: 999, background: color, transition: 'width 0.25s ease' }} />
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        padding: '7px 13px', borderRadius: '10px 10px 0 0', border: '1px solid var(--border-color,#e5e7eb)', borderBottom: active ? '1px solid var(--bg-card,#fff)' : '1px solid var(--border-color,#e5e7eb)',
        background: active ? 'var(--bg-card,#fff)' : 'var(--bg-soft,#f6f8fc)', color: active ? 'var(--accent)' : 'var(--text-secondary,#64748b)',
        fontWeight: 800, fontSize: 12.5, cursor: 'pointer', position: 'relative', top: 1,
      }}>{children}</button>
  );
}

function Pill({ children, color }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 800, color, background: `${color}18`, border: `1px solid ${color}33`, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' }}>{children}</span>;
}

function miniBtn(color, disabled) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 10,
    border: `1px solid ${color}40`, background: `${color}12`, color, fontWeight: 800, fontSize: 12,
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap',
  };
}

const panelWrap = {
  display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%',
  background: 'var(--bg-card,#fff)', border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 16, overflow: 'hidden',
};

export { keyOf as vehicleDetailKeyOf };
